import type { IconName } from '../ui';

/** One line of the progress log: what the assistant did while answering (#474). */
export interface ProgressStep {
  kind: string;
  label: string;
  detail?: string | null;
  link?: string | null;
  /** A short line under the step, e.g. what a finding says. */
  note?: string | null;
}

/** A progress event as the stream sends it and the history stores it. */
export interface ProgressEvent {
  kind?: string | null;
  label?: string | null;
  detail?: string | null;
  link?: string | null;
  note?: string | null;
  message?: string;
  search_done?: boolean;
}

interface KindLook {
  icon: IconName;
  /** Shown in green: what the assistant found, not what it did. */
  found?: boolean;
  /** Singular and plural words when the folded summary counts this kind. */
  count?: [string, string];
}

/**
 * How each kind of step looks. The backend names the steps (`label`), so renaming
 * one needs no change here; a kind missing from this table gets the default look and
 * is not counted, so a new step shows up before anyone adds it here.
 */
export const KIND_LOOKS: Record<string, KindLook> = {
  plan: { icon: 'sparkle' },
  search: { icon: 'search', count: ['search', 'searches'] },
  browse: { icon: 'grid' },
  read: { icon: 'file-lines', count: ['page opened', 'pages opened'] },
  found: { icon: 'check-circle', found: true, count: ['finding', 'findings'] },
  check: { icon: 'layers' },
  write: { icon: 'pencil' },
};

export const DEFAULT_LOOK: KindLook = { icon: 'chevron-right' };

export function lookFor(kind: string): KindLook {
  return KIND_LOOKS[kind] ?? DEFAULT_LOOK;
}

/**
 * The log lines among a turn's progress events. Events without a kind come from the
 * pipeline before #474 (free-text lines such as "Connecting the dots...") and are left
 * out, so an old conversation shows no log, as before. A search's "done" event repeats
 * its start and is left out too.
 */
export function toSteps(events: ProgressEvent[] | null | undefined): ProgressStep[] {
  return (events ?? [])
    .filter((e) => e.kind && e.label && !e.search_done)
    .map((e) => ({
      kind: e.kind as string,
      label: e.label as string,
      detail: e.detail ?? null,
      link: e.link ?? null,
      note: e.note ?? null,
    }));
}

/** A log line: a step, or several steps in a row that only repeat it (`n` of them). */
export interface ProgressLine {
  step: ProgressStep;
  n: number;
}

/**
 * The lines the log shows. Steps in a row with the same kind and label and nothing
 * else to say, such as several docs searches, become one line with a count.
 */
export function toLines(steps: ProgressStep[]): ProgressLine[] {
  const lines: ProgressLine[] = [];
  for (const step of steps) {
    const last = lines[lines.length - 1];
    const bare = !step.detail && !step.link && !step.note;
    if (
      last &&
      bare &&
      !last.step.detail &&
      !last.step.link &&
      !last.step.note &&
      last.step.kind === step.kind &&
      last.step.label === step.label
    ) {
      last.n += 1;
    } else {
      lines.push({ step, n: 1 });
    }
  }
  return lines;
}

/** "(3 searches)" after a merged line, when its kind is counted. */
export function lineCount(line: ProgressLine): string {
  const words = lookFor(line.step.kind).count;
  if (line.n < 2 || !words) return '';
  return `(${line.n} ${words[1]})`;
}

/** The folded log's one line, e.g. "3 searches · 5 pages read · 2 findings". */
export function summarize(steps: ProgressStep[]): string {
  const counts = new Map<string, number>();
  for (const step of steps) {
    if (lookFor(step.kind).count) counts.set(step.kind, (counts.get(step.kind) ?? 0) + 1);
  }
  const parts = Object.keys(KIND_LOOKS)
    .filter((kind) => counts.has(kind))
    .map((kind) => {
      const n = counts.get(kind) ?? 0;
      const [one, many] = KIND_LOOKS[kind].count as [string, string];
      return `${n} ${n === 1 ? one : many}`;
    });
  return parts.length > 0 ? parts.join(' · ') : `${steps.length} steps`;
}
